import { useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ContactForm from "@/components/forms/ContactForm";
import ClientIntakeForm from "@/components/forms/ClientIntakeForm";
import EmployerPartnershipForm from "@/components/forms/EmployerPartnershipForm";
import VolunteerApplicationForm from "@/components/forms/VolunteerApplicationForm";
import FAQ from "@/components/forms/FAQ";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Facebook, 
  X, 
  Linkedin, 
  Instagram,
  FileQuestion,
  HelpCircle,
  Users,
  ClipboardCheck,
  ArrowLeft
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function ContactPage() {
  const [activeForm, setActiveForm] = useState<string | null>(null);

  return (
    <div className="min-h-screen flex flex-col" id="contact">
      <Navbar />
      <main className="flex-grow bg-white">
        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-extrabold sm:text-5xl md:text-6xl text-slate-900">Contact Us</h1>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                We're here to answer your questions and provide the support you need.
              </p>
            </div>

            {/* Send Us a Message Form */}
            <div className="max-w-2xl mx-auto mb-12">
              {activeForm === 'client-intake' && (
                <div>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveForm(null)}
                    className="mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Contact
                  </Button>
                  <ClientIntakeForm />
                </div>
              )}
              {activeForm === 'employer-partnership' && (
                <div>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveForm(null)}
                    className="mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Contact
                  </Button>
                  <EmployerPartnershipForm />
                </div>
              )}
              {activeForm === 'volunteer-application' && (
                <div>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveForm(null)}
                    className="mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Contact
                  </Button>
                  <VolunteerApplicationForm />
                </div>
              )}
              {activeForm === 'faq' && (
                <div>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveForm(null)}
                    className="mb-4"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Contact
                  </Button>
                  <FAQ />
                </div>
              )}
              {!activeForm && <ContactForm />}
            </div>

            {/* Quick Links */}
            {!activeForm && (
              <div className="max-w-4xl mx-auto mb-16">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Quick Links</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <button 
                    onClick={() => setActiveForm('client-intake')}
                    className="flex items-center p-4 bg-white border border-slate-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 transition-colors cursor-pointer shadow-sm"
                  >
                    <ClipboardCheck className="h-6 w-6 text-primary-600 mr-3" />
                    <span className="text-sm font-medium text-slate-700">Client Intake Form</span>
                  </button>
                  <button 
                    onClick={() => setActiveForm('employer-partnership')}
                    className="flex items-center p-4 bg-white border border-slate-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 transition-colors cursor-pointer shadow-sm"
                  >
                    <Users className="h-6 w-6 text-primary-600 mr-3" />
                    <span className="text-sm font-medium text-slate-700">Employer Partnership</span>
                  </button>
                  <button 
                    onClick={() => setActiveForm('volunteer-application')}
                    className="flex items-center p-4 bg-white border border-slate-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 transition-colors cursor-pointer shadow-sm"
                  >
                    <FileQuestion className="h-6 w-6 text-primary-600 mr-3" />
                    <span className="text-sm font-medium text-slate-700">Volunteer Application</span>
                  </button>
                  <button 
                    onClick={() => setActiveForm('faq')}
                    className="flex items-center p-4 bg-white border border-slate-200 rounded-lg hover:bg-primary-50 hover:border-primary-200 transition-colors cursor-pointer shadow-sm"
                  >
                    <HelpCircle className="h-6 w-6 text-primary-600 mr-3" />
                    <span className="text-sm font-medium text-slate-700">Frequently Asked Questions</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
                <Card className="bg-slate-50 p-8 mb-8">
                  <ul className="space-y-6">
                    <li className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-100 text-primary-600">
                          <Phone className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-slate-900">Phone</h3>
                        <p className="mt-1 text-slate-500">(319) 493-7201</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-100 text-primary-600">
                          <Mail className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-slate-900">Email</h3>
                        <p className="mt-1 text-slate-500">contact@staffability.org</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-100 text-primary-600">
                          <MapPin className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-slate-900">Address</h3>
                        <p className="mt-1 text-slate-500">315 E 5th St. STE 202</p>
                        <p className="text-slate-500">Waterloo, IA 50703</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-100 text-primary-600">
                          <Clock className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-slate-900">Office Hours</h3>
                        <p className="mt-1 text-slate-500">Monday - Friday: 9am - 5pm</p>
                        <p className="text-slate-500">Saturday: 10am - 2pm</p>
                        <p className="text-slate-500">Sunday: Closed</p>
                      </div>
                    </li>
                  </ul>
                </Card>
              </div>
              
              {/* How We Can Help */}
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">How We Can Help</h2>
                <div className="bg-white p-6 rounded-lg border border-slate-200">
                  <ul className="space-y-3 text-slate-600">
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      Job placement and career counseling
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      Skills training and development programs
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      Housing and support services
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      Employer partnerships and recruitment
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      Volunteer opportunities and community involvement
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Connect With Us - Footer Style */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto text-center">
            <h3 className="text-lg font-medium text-slate-900 mb-4">Connect With Us</h3>
            <div className="flex justify-center space-x-6">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-100 rounded-full text-slate-600 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                <Facebook className="h-6 w-6" />
                <span className="sr-only">Facebook</span>
              </a>
              <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-100 rounded-full text-slate-600 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                <X className="h-6 w-6" />
                <span className="sr-only">X (Twitter)</span>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-100 rounded-full text-slate-600 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                <Linkedin className="h-6 w-6" />
                <span className="sr-only">LinkedIn</span>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-100 rounded-full text-slate-600 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                <Instagram className="h-6 w-6" />
                <span className="sr-only">Instagram</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
